import React from 'react';

import { Col } from '@opendoor/atoms/box';
import { Button } from '@opendoor/atoms/button';
import { Link } from '@opendoor/atoms/link';
import { Text } from '@opendoor/atoms/text';

export interface ISetupAccountFormProps {
  /**
   * Name of the form in analytics events, should be unique within the app
   * Convention: {appName}-{accountFormName} or {appName}-{resetPasswordFormName}
   */
  analyticsName: string;
  email: string;
  hasGeneratedPassword?: boolean;
  onResetValidEmail: (e?: React.SyntheticEvent) => void;
  requestResetPassword?: (email: string) => void;
}

export const CheckEmailForm: React.FC<ISetupAccountFormProps> = ({
  hasGeneratedPassword,
  email,
  analyticsName,
  onResetValidEmail,
  requestResetPassword,
}) => {
  const headerText = hasGeneratedPassword ? 'Set up your account' : 'Check your email',
    emailInstructionText = hasGeneratedPassword
      ? `First, check your email and click the link to set up your password. We emailed a link to ${email}.`
      : `We’ve sent reset password instructions to ${email}. Once you’ve reset your password, sign in again.`;

  return (
    <Col className="gap-10">
      <Col className="gap-5">
        <Text as="h2" className="text-header-md text-od-content-primary">
          {headerText}
        </Text>
        <Text as="p" className="text-body-sm text-od-content-primary">
          {emailInstructionText}
        </Text>
      </Col>

      {hasGeneratedPassword ? (
        <>
          {requestResetPassword && (
            <Button
              variant="primary"
              size="sm"
              // eslint-disable-next-line react-perf/jsx-no-new-function-as-prop -- Button isn't memoized, so a fresh handler identity is harmless.
              onClick={() => requestResetPassword(email)}
              analyticsName={`${analyticsName}-resend-reset-password-email`}
              aria-label="Resend email"
            >
              Resend email
            </Button>
          )}
          <Text as="p" className="text-body-md text-od-content-primary">
            We’ve sent reset password instructions to {email}. Once you’ve reset your password,{' '}
            <Link
              href="/login"
              onClick={onResetValidEmail}
              analyticsName={`${analyticsName}-sign-in-after-reset-password`}
              aria-label="Sign In"
              data-testid="sign-in-after-reset-password"
            >
              sign in again
            </Link>
          </Text>
        </>
      ) : (
        <Button
          variant="primary"
          size="sm"
          onClick={onResetValidEmail}
          analyticsName={`${analyticsName}-sign-in-after-reset-password`}
          aria-label="Sign In"
          data-testid="sign-in-after-reset-password"
        >
          Sign In
        </Button>
      )}
    </Col>
  );
};
